import AdminTable from '../../../components/admin/AdminTable';
﻿import React, { useState, useEffect, useCallback } from 'react';
import Loading from '../../../components/Loading';
import { Edit, Trash2, History, Plus, Notebook } from 'lucide-react';
import {
    fetchCategorias,
    Categoria
} from '../../../services/blogCategorias';
import AddBlogCategoria from '../../../components/BlogCategoria/AddBlogCategoria';
import EditBlogCategoria from '../../../components/BlogCategoria/EditBlogCategoria';
import DeleteBlogCategoria from '../../../components/BlogCategoria/DeleteBlogCategoria';
import Pagination from '../../../components/Pagination/Pagination';
import ExpiredSessionModal from '../../../components/Expired/ExpiredSessionModal';
import { useSessionExpiration } from '../../../hooks/useSessionExpiration';
import { useAuth } from '../../../context/AuthContext';
import { useModelPermission } from '../../../hooks/useModelPermission';

const BlogCategoriaPage: React.FC = () => {
    const [categorias, setCategorias] = useState<Categoria[]>([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    // Estados para modales
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedCategoria, setSelectedCategoria] = useState<Categoria | null>(null);

    const { isSessionExpired, setIsSessionExpired } = useSessionExpiration();
    const { logout, user } = useAuth();

    const userLevelId = user?.user_level_id ?? 0;
    const perm = useModelPermission('categoriablog', userLevelId); // Ajusta el nombre del modelo según tus permisos

    const loadCategorias = useCallback(async () => {
        if (!perm?.can_read) {
            setCategorias([]);
            setLoading(false);
            return;
        }
        setLoading(true);
        try {
            const data = await fetchCategorias();
            setCategorias(data);
        } catch (error) {
            /* Browser diagnostics omitted. */
        } finally {
            setLoading(false);
        }
    }, [perm?.can_read]);

    useEffect(() => {
        loadCategorias();
    }, [loadCategorias]);

    const totalPages = Math.ceil(categorias.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const displayedCategorias = categorias.slice(startIndex, startIndex + itemsPerPage);

    const handleRefresh = () => {
        loadCategorias();
    };

    const handleAddClick = () => {
        setShowAddModal(true);
    };

    const handleEditClick = (categoria: Categoria) => {
        setSelectedCategoria(categoria);
        setShowEditModal(true);
    };

    const handleDeleteClick = (categoria: Categoria) => {
        setSelectedCategoria(categoria);
        setShowDeleteModal(true);
    };

    const handleModalSuccess = () => {
        loadCategorias();
    };

    if (perm === null || (loading && categorias.length === 0)) return <Loading text="Cargando categorías..." />;

    return (
        <div data-admin-page="true" className="space-y-6 mx-auto px-4 sm:px-6 lg:px-8 max-w-9xl">
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-xl font-bold text-gray-800 admin-page-title">Gestión de <span className="admin-title-accent">Categorías de Blogs</span></h2>
                    <div className="mt-1 flex items-center space-x-2 text-sm text-gray-500">
                        <a href="/dashboard" className="hover:text-[#c1348b]">Dashboard</a>
                        <span>/</span>
                        <a href="/blog-categoria" className="text-[#c1348b] hover:text-[#8f1f64]">Categoría de Blogs</a>
                    </div>
                </div>

                <div className="flex gap-2 items-center">
                    <button
                        onClick={handleRefresh}
                        style={{ height: '39px' }}
                        className="bg-[#61257b] text-white px-4 py-2 rounded-lg hover:bg-[#4a1c5e] flex items-center text-sm font-medium transition-colors"
                        title="Recargar Categorías"
                    >
                        <History className="w-5 h-5" />
                    </button>
                    {perm.can_create && (
                        <button
                            onClick={handleAddClick}
                            style={{ minWidth: '170.75px', height: '39px' }}
                            className="bg-[#c1348b] text-white px-4 py-2 rounded-lg hover:bg-[#8f1f64] flex items-center text-sm font-medium transition-colors"
                        >
                            <Plus className="w-5 h-5 mr-2" />
                            Agregar Categoría
                        </button>
                    )}
                </div>
            </div>

            <div className="bg-white rounded-lg shadow overflow-hidden">
                <ExpiredSessionModal
                    isOpen={isSessionExpired}
                    onClose={async () => {
                        setIsSessionExpired(false);
                        await logout();
                    }}
                />

                {showAddModal && (
                    <AddBlogCategoria
                        isOpen={showAddModal}
                        onClose={() => setShowAddModal(false)}
                        onSuccess={handleModalSuccess}
                    />
                )}
                {showEditModal && selectedCategoria && (
                    <EditBlogCategoria
                        isOpen={showEditModal}
                        categoria={selectedCategoria}
                        onClose={() => {
                            setShowEditModal(false);
                            setSelectedCategoria(null);
                        }}
                        onSuccess={handleModalSuccess}
                    />
                )}
                {showDeleteModal && selectedCategoria && (
                    <DeleteBlogCategoria
                        isOpen={showDeleteModal}
                        categoria={selectedCategoria}
                        onClose={() => {
                            setShowDeleteModal(false);
                            setSelectedCategoria(null);
                        }}
                        onSuccess={handleModalSuccess}
                    />
                )}

                <div className="overflow-x-auto">
                    <AdminTable className="w-full">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nombre</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Slug</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Descripción</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha de Creación</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cantidad de Publicaciones</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acciones</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {loading ? (
                                <tr>
                                    <td colSpan={6} className="px-6 py-12 text-center">
                                        <div className="flex flex-col items-center justify-center space-y-3">
                                            <Notebook className="w-12 h-12 text-gray-400" />
                                            <span className="text-lg font-medium text-gray-600">
                                                Cargando categorías...
                                            </span>
                                        </div>
                                    </td>
                                </tr>
                            ) : perm.can_read ? (
                                displayedCategorias.length === 0 ? (
                                    <tr>
                                        <td colSpan={6} className="px-6 py-12 text-center">
                                            <div className="flex flex-col items-center justify-center space-y-3">
                                                <Notebook className="w-12 h-12 text-gray-400" />
                                                <span className="text-lg font-medium text-gray-600">
                                                    No hay categorías disponibles
                                                </span>
                                                {perm.can_create && (
                                                    <p className="text-gray-500 text-sm">
                                                        Agrega una nueva categoría para comenzar
                                                    </p>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    displayedCategorias.map((categoria) => (
                                        <tr key={categoria.id} className="hover:bg-gray-50">
                                            <td className="px-4 py-4 text-sm text-gray-900">{categoria.nombre}</td>
                                            <td className="px-4 py-4 text-sm text-gray-900">{categoria.slug}</td>
                                            <td className="px-4 py-4 text-sm text-gray-900 truncate max-w-xs">
                                                {categoria.descripcion || '-'}
                                            </td>
                                            <td className="px-4 py-4 text-sm text-gray-500">
                                                {new Date(categoria.creado_en).toLocaleDateString()}
                                            </td>
                                            <td className="px-4 py-4 text-sm text-gray-500">{categoria.cantidad_publicaciones}</td>
                                            <td className="px-4 py-4">
                                                <div className="flex gap-2">
                                                    {perm.can_update && (
                                                        <button
                                                            onClick={() => handleEditClick(categoria)}
                                                            className="p-1.5 rounded-full bg-blue-50 hover:bg-blue-100 text-[#c1348b] hover:text-[#8f1f64] transition"
                                                            title="Editar"
                                                        >
                                                            <Edit className="w-4 h-4" />
                                                        </button>
                                                    )}
                                                    {perm.can_delete && (
                                                        <button
                                                            onClick={() => handleDeleteClick(categoria)}
                                                            className="p-1.5 rounded-full bg-red-50 hover:bg-red-100 text-red-600 hover:text-red-800 transition"
                                                            title="Eliminar"
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )
                            ) : (
                                <tr>
                                    <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                                        No tienes permiso para ver las categorías.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </AdminTable>
                </div>

                {!loading && perm.can_read && categorias.length > 0 && (
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                        itemsPerPage={itemsPerPage}
                        totalItems={categorias.length}
                    />
                )}
            </div>
        </div>
    );
};

export default BlogCategoriaPage;
